import React, { useState } from 'react';
import { FaUser, FaShieldAlt, FaEthereum, FaKey, FaGlobe, FaIdCard, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { copyToClipboard } from '../../utils/helpers';
import '../../css/user-profile.css';

export const UserProfile = ({ user }) => {
  const [copiedField, setCopiedField] = useState(null);

  const userName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Anonymous';
  const isVerified = user.verifiedStatusID === 1;

  const handleCopy = async (text, fieldName) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const InfoField = ({ label, value, icon, fieldName, color = '#6366f1' }) => {
    return (
      <div className="mb-4">
        <div className="user-profile-field-label">
          {icon && (
            <span className="user-profile-field-icon" style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}>
              {icon}
            </span>
          )}
          {label}
        </div>
        {value ? (
          <code
            className={`d-block p-4 user-profile-field-value ${copiedField === fieldName ? 'copied' : 'default'}`}
            onClick={() => handleCopy(value, fieldName)}
            title="Click to copy"
          >
            {value}
          </code>
        ) : (
          <div className="d-block p-4 user-profile-field-unavailable">
            Unavailable
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-profile-container">
      {/* Hero Section */}
      <div className="mb-5">
        <div className="d-flex align-items-center mb-4">
          <div className={`user-profile-avatar ${isVerified ? 'verified' : 'unverified'}`}>
            {userName.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h1 className="mb-2 user-profile-name">
              {userName}
            </h1>
            <div className="d-flex align-items-center gap-3 mb-3">
              {isVerified && (
                <span className="user-profile-badge-verified">
                  <FaShieldAlt />
                  Verified User
                </span>
              )}
              <span className="user-profile-badge-id">
                <FaIdCard />
                ID: {user.createdByWalletUserRegistrationID}
              </span>
            </div>
            <p className="user-profile-description">
              <FaUser className="me-2" />
              Blockchain Network User Profile
            </p>
          </div>
        </div>

        {/* Verification Status */}
        {user.verifiedStatus && (
          <div className="user-profile-verification-section">
            <h6 className="user-profile-verification-title">
              <FaShieldAlt className="me-2" />
              Verification Status
            </h6>
            <p className="user-profile-verification-text">
              {user.verifiedStatus}
            </p>
          </div>
        )}
      </div>

      {/* Crypto Addresses Section */}
      <div className="user-profile-section user-profile-section-crypto">
        <h4 className="user-profile-section-title crypto">
          <span className="user-profile-section-icon crypto">
            <FaKey />
          </span>
          Blockchain Addresses
        </h4>
        <div className="row">
          <div className="col-md-6">
            <InfoField
              label="Ethereum Address"
              value={user.publicKeyEth}
              icon={<FaEthereum />}
              fieldName="publicKeyEth"
              color="#627eea"
            />
            <InfoField
              label="Identity URL"
              value={user.rootIdentityUrl}
              icon={<FaGlobe />}
              fieldName="rootIdentityUrl"
              color="#0ea5e9"
            />
            <InfoField
              label="O-ID (Operate Key)"
              value={user.operateID}
              icon={<FaKey />}
              fieldName="operateID"
              color="#8b5cf6"
            />
          </div>
          <div className="col-md-6">
            <InfoField
              label="Accumulate Address"
              value={user.publicKeyAccumulate}
              icon={<FaKey />}
              fieldName="publicKeyAccumulate"
              color="#059669"
            />
            <InfoField
              label="Accumulate Lite Account"
              value={user.liteAccountKeyAccumulate}
              icon={<FaKey />}
              fieldName="liteAccountKeyAccumulate"
              color="#0d9488"
            />
          </div>
        </div>
      </div>

      {/* Security Key Section */}
      {user.publicKeyHash && (
        <div className="user-profile-section user-profile-section-security">
          <h4 className="user-profile-section-title security">
            <span className="user-profile-section-icon security">
              <FaShieldAlt />
            </span>
            Security Access Key
          </h4>
          <InfoField
            label="Private Security Hash"
            value={user.publicKeyHash}
            fieldName="publicKeyHash"
            color="#10b981"
          />
        </div>
      )}

      {/* Contact Information */}
      {(user.emailAddress || user.phone || user.country) && (
        <div className="user-profile-section user-profile-section-contact">
          <h4 className="user-profile-section-title contact">
            <span className="user-profile-section-icon contact">
              <FaEnvelope />
            </span>
            Contact Information
          </h4>
          <div className="row">
            {user.emailAddress && (
              <div className="col-md-6 mb-3">
                <div className="user-profile-contact-label">
                  <FaEnvelope />
                  Email Address
                </div>
                <div className="user-profile-contact-value">{user.emailAddress}</div>
              </div>
            )}
            {user.phone && (
              <div className="col-md-6 mb-3">
                <div className="user-profile-contact-label">
                  <FaPhone />
                  Phone Number
                </div>
                <div className="user-profile-contact-value">{user.phone}</div>
              </div>
            )}
            {user.country && (
              <div className="col-md-6 mb-3">
                <div className="user-profile-contact-label">
                  <FaMapMarkerAlt />
                  Country
                </div>
                <div className="user-profile-contact-value">{user.country}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Social Media */}
      {(user.socialTwitter || user.socialTelegram || user.socialLinkedIn || user.socialDiscord || user.socialYouTube || user.socialInstagram || user.gitHub) && (
        <div className="mt-4">
          <h5 className="mb-3">Social Media</h5>
          <div className="row">
            {user.socialTwitter && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">Twitter</small>
                <div>{user.socialTwitter}</div>
              </div>
            )}
            {user.socialTelegram && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">Telegram</small>
                <div>{user.socialTelegram}</div>
              </div>
            )}
            {user.socialLinkedIn && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">LinkedIn</small>
                <div>{user.socialLinkedIn}</div>
              </div>
            )}
            {user.socialDiscord && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">Discord</small>
                <div>{user.socialDiscord}</div>
              </div>
            )}
            {user.socialYouTube && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">YouTube</small>
                <div>{user.socialYouTube}</div>
              </div>
            )}
            {user.socialInstagram && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">Instagram</small>
                <div>{user.socialInstagram}</div>
              </div>
            )}
            {user.gitHub && (
              <div className="col-md-4 mb-3">
                <small className="text-muted">GitHub</small>
                <div>{user.gitHub}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Information */}
      {(user.note || user.comments) && (
        <div className="mt-4">
          <h5 className="mb-3">Additional Information</h5>
          {user.note && (
            <div className="mb-3">
              <small className="text-muted">Note</small>
              <div>{user.note}</div>
            </div>
          )}
          {user.comments && (
            <div className="mb-3">
              <small className="text-muted">Comments</small>
              <div>{user.comments}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};